import { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

export default function LiveChannel({ navigate, setPending }) {
  const [scanning, setScanning] = useState(false)
  const [signal,   setSignal]   = useState(null)
  const [error,    setError]    = useState(null)
  const intervalRef = useRef(null)

  function startScan() {
    setScanning(true)
    setSignal(null)
    setError(null)
    intervalRef.current = setInterval(poll, 2000)
    poll()
  }

  function stopScan() {
    setScanning(false)
    clearInterval(intervalRef.current)
  }

  useEffect(() => () => clearInterval(intervalRef.current), [])

  async function poll() {
    try {
      const res = await fetch('/api/pos/latest')
      if (res.ok) {
        const data = await res.json()
        if (data?.merchant_name) { setSignal(data); stopScan() }
      }
    } catch {
      // offline — simulate finding a signal after 3s for demo
    }
  }

  // Demo: simulate detecting a signal after 3s if no server
  function demoDetect() {
    setScanning(true)
    setSignal(null)
    setTimeout(() => {
      setSignal({
        merchant_name: 'Ridgefield Eye Associates',
        total: 142.50, tax: 11.38, persona: 'FAMILY',
        broadcast_id: 'BC-' + Math.random().toString(36).substring(2,8).toUpperCase(),
        items: [
          { sku:'EXAM-001', description:'Comprehensive Eye Exam',      quantity:1, unit_price:95.00,  total_price:95.00  },
          { sku:'CLT-090',  description:'Contact Lens Trial Pack',     quantity:1, unit_price:22.00,  total_price:22.00  },
          { sku:'DRP-200',  description:'Preservative-Free Eye Drops', quantity:2, unit_price:12.75,  total_price:25.50  },
        ],
      })
      setScanning(false)
    }, 2400)
  }

  function acceptSignal() {
    setPending(signal)
    navigate('capture')
  }

  return (
    <div className="min-h-full bg-[#080c10] px-5 pt-5">
      <div className="sticky top-0 z-10 pb-3 bg-gradient-to-b from-[#080c10] to-transparent">
        <h1 className="text-xl font-black">📶 Live POS Channel</h1>
        <p className="text-xs text-white/30 mt-0.5">Detect nearby merchant receipt signals</p>
      </div>

      <AnimatePresence mode="wait">
        {!scanning && !signal && (
          <motion.div key="idle" initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} className="flex flex-col items-center pt-8 gap-4">
            <div className="w-28 h-28 rounded-full flex items-center justify-center text-5xl glass-card">📶</div>
            <p className="text-sm text-white/30 text-center">Scan for active POS broadcasts<br/>from nearby merchants.</p>
            <button onClick={startScan} className="btn-green max-w-xs">Start Scanning</button>
            <button onClick={demoDetect} className="btn-ghost max-w-xs">Demo Detect Signal</button>
          </motion.div>
        )}

        {scanning && (
          <motion.div key="scanning" initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
            className="flex flex-col items-center pt-10 gap-5">
            {/* Radar animation */}
            <div className="relative w-32 h-32 flex items-center justify-center">
              {[0,1,2].map(i => (
                <motion.div key={i} className="absolute rounded-full border border-[#00ff87]"
                  style={{width:`${(i+1)*33}%`,height:`${(i+1)*33}%`}}
                  animate={{scale:[1,1.4],opacity:[0.7,0]}}
                  transition={{duration:1.8,repeat:Infinity,delay:i*0.4,ease:'easeOut'}}/>
              ))}
              <div className="w-12 h-12 rounded-full bg-[rgba(0,255,135,0.1)] border border-[rgba(0,255,135,0.3)] flex items-center justify-center">
                <span className="text-xl">📶</span>
              </div>
            </div>
            <motion.p className="text-sm text-white/40" animate={{opacity:[0.4,1,0.4]}} transition={{duration:1.4,repeat:Infinity}}>
              Scanning for signals…
            </motion.p>
            <button onClick={stopScan} className="btn-ghost max-w-xs">Stop</button>
          </motion.div>
        )}

        {signal && (
          <motion.div key="signal" initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} exit={{opacity:0}} className="pt-2">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-2 h-2 rounded-full bg-[#00ff87] animate-pulse"/>
              <span className="text-xs font-bold tracking-widest uppercase text-[#00ff87]">Signal Detected</span>
            </div>

            <div className="glass-card p-5 mb-3">
              <div className="text-lg font-black mb-0.5">{signal.merchant_name}</div>
              <div className="text-3xl font-black text-[#00ff87] mt-2 mb-1">${parseFloat(signal.total).toFixed(2)}</div>
              <div className="text-xs text-white/30">Broadcast ID: <span className="font-mono">{signal.broadcast_id}</span></div>
            </div>

            <div className="glass-card p-0 mb-4 overflow-hidden">
              <div className="px-4 py-3 border-b border-white/[0.06]">
                <span className="text-[11px] font-bold tracking-widest uppercase text-white/30">{signal.items.length} Items</span>
              </div>
              {signal.items.map((item, i) => (
                <div key={i} className={`flex justify-between items-start gap-3 px-4 py-3 ${i<signal.items.length-1?'border-b border-white/[0.04]':''}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold">{item.description}</div>
                    <div className="sku-mono">SKU {item.sku} · qty {item.quantity}</div>
                  </div>
                  <div className="text-sm font-bold text-[#00ff87]">${item.total_price.toFixed(2)}</div>
                </div>
              ))}
            </div>

            <button onClick={acceptSignal} className="btn-green mb-2">📡 Accept & Suck Receipt</button>
            <button onClick={() => { setSignal(null) }} className="btn-ghost">Ignore Signal</button>
          </motion.div>
        )}
      </AnimatePresence>
      <div className="h-4"/>
    </div>
  )
}
